import { useEffect, useState } from 'react'
import WithDrawWalletModal from 'components/modals/withDrawWallets'
import BankAccountCard from 'components/bankAccountCard'
import useGetCards from 'pages/loans/withDraw/hooks/useGetCards'
import { faFull, faPlain, toman } from 'utilities/helper/format'
import { IGuarantorOption } from 'components/modals/guarantorPicker'
import StickyAction from '../components/StickyAction'
import { ITerms } from './StepTerms'

function Line({
    label,
    value,
    tone,
}: {
    label: string
    value: string
    tone?: 'strong'
}) {
    return (
        <div className=" flex items-start justify-between gap-3 border-b border-divider py-2 last:border-b-0">
            <span className=" shrink-0 text-[11px] text-text-secondary">
                {label}
            </span>
            <span
                className={` text-left ${
                    tone === 'strong'
                        ? ' text-sm font-bold text-heading'
                        : ' text-[11px] font-bold text-text-body'
                }`}
            >
                {value}
            </span>
        </div>
    )
}

/**
 * Step 5 — the receipt, and where the loan actually happens.
 *
 * Two outcomes live here and they are genuinely different, so they are two
 * buttons rather than one button and a toggle:
 *
 *  - **اعطای وام** moves the money through Hamyan to the member's account.
 *  - **فقط ثبت شود** records a loan the manager is handing over themselves.
 *    The instalment schedule, the reminders and the fund's books are
 *    identical; only the payment is not ours to make.
 *
 * The destination account is asked for here rather than with the amount
 * because it belongs to the payout, and it is only needed for the first of
 * those two buttons.
 */
export default function StepReview({
    borrowerName,
    borrowerPoolshipId,
    borrowerMemberId,
    fundName,
    amount,
    terms,
    guarantors,
    note,
    onNoteChange,
    onCardChange,
    selectedCard,
    requestedCardId,
    isSubmitting,
    onGrant,
    onRecordOnly,
}: {
    borrowerName?: string
    borrowerPoolshipId?: number
    borrowerMemberId?: number
    fundName?: string
    amount: number
    terms: ITerms
    guarantors: IGuarantorOption[]
    note: string
    onNoteChange: (next: string) => void
    onCardChange: (card: IBankAccountCards | undefined) => void
    selectedCard?: IBankAccountCards
    /** the account the borrower named in their loan request, if they filed one */
    requestedCardId?: number
    isSubmitting: boolean
    onGrant: () => void
    onRecordOnly: () => void
}) {
    const [isPickingCard, setIsPickingCard] = useState(false)
    const [, setSelectedBankNumber] = useState<number>(0)

    const {
        data,
        isLoading,
        activeAccounts,
        otherAccounts,
        handleSelectCard,
        reallySelectedCard,
    } = useGetCards(
        borrowerPoolshipId,
        borrowerMemberId,
        setSelectedBankNumber,
        requestedCardId
    )

    // the member answered «به کدام حساب؟» themselves when they asked; opening
    // on any other account of theirs would be the app overruling them
    const showsRequestedCard =
        !!requestedCardId && reallySelectedCard?.id === requestedCardId

    // useGetCards owns the selection; mirror it up so the payload has it.
    // In an effect, not in render: calling the parent's setter while
    // rendering is a React warning at best and a render loop at worst.
    useEffect(() => {
        if (reallySelectedCard?.id !== selectedCard?.id) {
            onCardChange(reallySelectedCard)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reallySelectedCard?.id])

    // accounts belonging to the BORROWER, as opposed to the manager's own
    const hasOwnAccount = (activeAccounts?.length ?? 0) > 0

    const count = Number(terms.installments || 0)
    const feeValue = Number(terms.fee || 0)
    const feeAmount =
        terms.feeType === 'percentage'
            ? Math.round((amount * feeValue) / 100)
            : feeValue

    return (
        <>
            <div className=" rounded-2xl border border-stroke bg-white p-4">
                <p className=" mb-1 text-xs font-bold text-brand-deep">
                    وامی که ثبت می‌شود
                </p>
                <Line label="مبلغ" value={toman(amount)} tone="strong" />
                <Line label="گیرنده" value={borrowerName || '—'} />
                <Line
                    label="اقساط"
                    value={`${faPlain.format(count)} قسط ماهانه`}
                />
                {feeAmount > 0 && (
                    <Line
                        label="کارمزد"
                        value={`${faFull.format(feeAmount)} تومان — ${
                            terms.feeMode === 'lip_pr'
                                ? 'کسر از مبلغ وام'
                                : 'افزودن به اقساط'
                        }`}
                    />
                )}
                {guarantors.length > 0 && (
                    <Line
                        label="ضامن"
                        value={guarantors.map((item) => item.name).join('، ')}
                    />
                )}
                {fundName && <Line label="صندوق" value={fundName} />}
            </div>

            {/* The account block serves BOTH buttons below, which is why the
                number is on the face of it rather than inside the picker.
                «اعطای وام و واریز» sends Hamyan's money here and the manager
                never reads the digits; «فقط ثبت شود» means the manager makes
                the transfer themselves, and then these digits are the only
                thing on the screen they actually need. */}
            <div className=" mt-3 rounded-2xl border border-stroke bg-white p-4">
                <p className=" mb-2 text-xs font-bold text-brand-deep">
                    حساب گیرندهٔ وام
                </p>

                {showsRequestedCard && (
                    <p className=" mb-2 rounded-lg bg-brand-soft px-2.5 py-1.5 text-[11px] font-bold text-brand-deep">
                        همان حسابی که خودش هنگام ثبت درخواست وارد کرده
                    </p>
                )}

                {reallySelectedCard ? (
                    <BankAccountCard
                        account={reallySelectedCard}
                        belongsToBorrower={
                            borrowerMemberId
                                ? reallySelectedCard.member === borrowerMemberId
                                : undefined
                        }
                        borrowerName={borrowerName}
                        onChange={() => setIsPickingCard(true)}
                        source="loan_grant_review"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => setIsPickingCard(true)}
                        className=" w-full rounded-xl bg-canvas px-3 py-2.5 text-right text-xs text-text-body"
                    >
                        {isLoading
                            ? 'در حال خواندن حساب‌ها…'
                            : 'انتخاب شماره حساب'}
                    </button>
                )}

                {/* Said here rather than discovered inside the picker. The
                    borrower's own account can only be registered by the
                    borrower, so the manager's three real options are spelled
                    out instead of leaving them in a modal with no accounts
                    in it. */}
                {!isLoading && !hasOwnAccount && (
                    <div className=" mt-2 rounded-lg bg-warning-soft px-2.5 py-2">
                        <p className=" text-[11px] font-bold text-warning-on">
                            {borrowerName
                                ? `${borrowerName} هنوز شماره حسابی ثبت نکرده`
                                : 'این عضو هنوز شماره حسابی ثبت نکرده'}
                        </p>
                        <p className=" mt-1 text-[11px] leading-5 text-warning-on">
                            پس شماره کارتی هم نداریم که نشانتان بدهیم. می‌توانید
                            به یکی از حساب‌های خودتان واریز کنید، یا از عضو
                            بخواهید در برنامه شماره حسابش را ثبت کند، یا پول را
                            دستی بدهید و «فقط ثبت شود» را بزنید.
                        </p>
                    </div>
                )}

                {reallySelectedCard && (
                    <p className=" mt-2 text-[11px] leading-5 text-text-secondary">
                        با «اعطای وام و واریز» پول از موجودی صندوق نزد همیان به
                        همین حساب می‌رود. اگر خودتان کارت‌به‌کارت می‌کنید، شماره
                        را کپی کنید و بعد «فقط ثبت شود» را بزنید.
                    </p>
                )}
            </div>

            <div className=" mt-3 rounded-2xl border border-stroke bg-white p-4">
                <label
                    htmlFor="loan-note"
                    className=" text-xs text-text-secondary"
                >
                    توضیحات (اختیاری)
                </label>
                <textarea
                    id="loan-note"
                    value={note}
                    onChange={(event) => onNoteChange(event.target.value)}
                    rows={2}
                    placeholder="مثلاً: بابت هزینهٔ درمان"
                    className=" mt-1 w-full resize-none rounded-xl bg-canvas px-3 py-2 text-xs outline-none placeholder:text-text-muted"
                />
            </div>

            {/* Pinned, not at the bottom of the page. The receipt, the account
                picker and the note push the two buttons well below the fold on
                a phone, and a manager who has finished the wizard should never
                have to hunt for the button that finishes it. */}
            <StickyAction>
                <div className=" space-y-2">
                    <button
                        type="button"
                        disabled={isSubmitting || !reallySelectedCard}
                        onClick={onGrant}
                        className=" w-full rounded-full bg-green-1 py-3 text-[15px] font-bold text-white transition-transform active:scale-[0.99] disabled:opacity-50 motion-reduce:transition-none"
                    >
                        {isSubmitting ? 'در حال ثبت…' : 'اعطای وام و واریز'}
                    </button>
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={onRecordOnly}
                        className=" w-full rounded-full border border-brand bg-white py-2.5 text-[13px] font-bold text-brand-deep disabled:opacity-50"
                    >
                        فقط ثبت شود (پول را خودم می‌دهم)
                    </button>
                </div>
            </StickyAction>

            <WithDrawWalletModal
                show={isPickingCard}
                onHide={() => setIsPickingCard(false)}
                // optional the whole way down: a member with no registered
                // account comes back as an empty `receivers`, and indexing
                // [0] blind takes the entire review step down with it
                data={data?.receivers?.[0]?.bankaccounts}
                activeAccounts={activeAccounts}
                otherAccounts={otherAccounts}
                selectedUser={borrowerName}
                setSelectedBankNumber={setSelectedBankNumber}
                handleSelectCard={handleSelectCard}
            />
        </>
    )
}
